import React from 'react';
import { useNavigate } from 'react-router-dom';
import { addUnit, normalizeAppUrl, stripHtml } from '../../utils/styleHelpers';
import { slugify } from '../../utils/helpers';
import { ECOMMERCE_BRANDING } from '../branding';

export const ShopBreadcrumbs = ({
    styleSettings = {},
    settings = {},
    activeCategory = 'All',
    activeSubCategory = 'All',
    setActiveCategory,
    setActiveSubCategory,
    isCategoryMatch,
    isSubCategoryMatch,
    primaryColor = '#3b82f6'
}) => {
    const navigate = useNavigate();

    const catMatchFn = (c1, c2) => {
        if (!c2 || c2 === 'All') return true;
        if (!c1) return false;
        if (c1 === c2) return true;
        const n1 = String(c1).toLowerCase().trim();
        const n2 = String(c2).toLowerCase().trim();
        return n1 === n2 || slugify(n1) === slugify(n2);
    };

    const isCatMatch = isCategoryMatch || catMatchFn;
    const isSubCatMatch = isSubCategoryMatch || catMatchFn;

    if (styleSettings.shopCategoryPathShow === false || styleSettings.shopCategoryPathShow === 'false') {
        return null;
    }

    const separator = styleSettings.shopCategoryPathSeparator || settings.shopCategoryPathSeparator || '›';
    const linkColor = styleSettings.shopCategoryPathLinkColor || settings.shopCategoryPathLinkColor || primaryColor;
    const activeColor = styleSettings.shopCategoryPathActiveColor || settings.shopCategoryPathActiveColor || '#0f172a';
    const textColor = styleSettings.shopCategoryPathColor || settings.shopCategoryPathColor || '#64748b';
    const fontWeight = styleSettings.shopCategoryPathWeight || settings.shopCategoryPathWeight || '600';
    const rootTitle = stripHtml(styleSettings.shopBreadcrumbRoot || settings.shopBreadcrumbRoot || 'Shop');

    const isAllRoot = isCatMatch(activeCategory, 'All') && isSubCatMatch(activeSubCategory, 'All');
    const showHomeLink = styleSettings.shopBreadcrumbShowHome === true || styleSettings.shopBreadcrumbShowHome === 'true';

    // If on "All Products" root view and no Home link is enabled, hide breadcrumbs to prevent duplicating the hero heading/subtitle
    if (isAllRoot && !showHomeLink && styleSettings.shopCategoryPathShowOnAll !== true && styleSettings.shopCategoryPathShowOnAll !== 'true') {
        return null;
    }

    const formatMultiUnit = (val) => {
        if (!val) return undefined;
        const str = String(val).trim();
        if (!str) return undefined;
        return str.split(/\s+/).map(v => addUnit(v)).join(' ');
    };

    const alignVal = styleSettings.shopCategoryPathAlign || settings.shopCategoryPathAlign || 'flex-start';

    return (
        <nav aria-label="Category breadcrumb" className="shop-category-breadcrumb" style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: alignVal,
            width: (alignVal && alignVal !== 'flex-start') ? '100%' : undefined,
            gap: '6px',
            fontSize: addUnit(styleSettings.shopCategoryPathSize || settings.shopCategoryPathSize || '0.85rem'),
            fontFamily: (styleSettings.shopCategoryPathFontFamily && styleSettings.shopCategoryPathFontFamily !== 'inherit') ? styleSettings.shopCategoryPathFontFamily : ((settings.shopCategoryPathFontFamily && settings.shopCategoryPathFontFamily !== 'inherit') ? settings.shopCategoryPathFontFamily : undefined),
            fontWeight: fontWeight,
            textTransform: styleSettings.shopCategoryPathTransform || settings.shopCategoryPathTransform || 'none',
            background: (styleSettings.shopCategoryPathBg && styleSettings.shopCategoryPathBg !== 'transparent') ? styleSettings.shopCategoryPathBg : undefined,
            padding: formatMultiUnit(styleSettings.shopCategoryPathPadding || settings.shopCategoryPathPadding),
            borderRadius: formatMultiUnit(styleSettings.shopCategoryPathRadius || settings.shopCategoryPathRadius),
            border: (styleSettings.shopCategoryPathBorder && styleSettings.shopCategoryPathBorder !== 'none') ? styleSettings.shopCategoryPathBorder : undefined,
            marginBottom: addUnit(styleSettings.shopCategoryPathMarginBottom || settings.shopCategoryPathMarginBottom || '20px'),
            flexWrap: 'wrap',
            color: textColor
        }}>
            {/* Optional Home crumb */}
            {showHomeLink && (
                <>
                    <button
                        type="button"
                        onClick={() => navigate(normalizeAppUrl('/'))}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            color: linkColor,
                            fontWeight: fontWeight,
                            fontSize: 'inherit',
                            fontFamily: 'inherit',
                            textTransform: 'inherit',
                            textDecoration: 'underline',
                            textDecorationStyle: 'dotted'
                        }}
                    >
                        Home
                    </button>
                    <span style={{ color: textColor, opacity: 0.7, margin: '0 2px' }}>
                        {separator}
                    </span>
                </>
            )}

            {/* Shop Root crumb */}
            {isAllRoot ? (
                <span style={{
                    color: activeColor,
                    fontWeight: fontWeight,
                    fontSize: 'inherit',
                    fontFamily: 'inherit',
                    textTransform: 'inherit'
                }}>
                    {rootTitle}
                </span>
            ) : (
                <button
                    type="button"
                    onClick={() => { 
                        if (setActiveCategory) setActiveCategory('All');
                        if (setActiveSubCategory) setActiveSubCategory('All');
                        navigate(normalizeAppUrl('/shop'), { preventScrollReset: true, state: { preventScrollReset: true } });
                    }}
                    style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        color: linkColor,
                        fontWeight: fontWeight,
                        fontSize: 'inherit',
                        fontFamily: 'inherit',
                        textTransform: 'inherit',
                        textDecoration: 'underline',
                        textDecorationStyle: 'dotted'
                    }}
                >
                    {rootTitle}
                </button>
            )}

            {/* Category crumb */}
            {!isCatMatch(activeCategory, 'All') && (
                <>
                    <span style={{ color: textColor, opacity: 0.7, margin: '0 2px' }}>
                        {separator}
                    </span>
                    {!isSubCatMatch(activeSubCategory, 'All') ? (
                        <button
                            type="button"
                            onClick={() => {
                                if (setActiveSubCategory) setActiveSubCategory('All');
                                navigate(normalizeAppUrl(`/products/${slugify(activeCategory)}`), { preventScrollReset: true, state: { preventScrollReset: true } });
                            }}
                            style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                color: linkColor,
                                fontWeight: fontWeight,
                                fontSize: 'inherit',
                                fontFamily: 'inherit',
                                textTransform: 'inherit',
                                textDecoration: 'underline',
                                textDecorationStyle: 'dotted'
                            }}
                        >
                            {stripHtml(activeCategory)}
                        </button>
                    ) : (
                        <span style={{
                            color: activeColor,
                            fontWeight: fontWeight,
                            fontSize: 'inherit',
                            fontFamily: 'inherit',
                            textTransform: 'inherit'
                        }}>
                            {stripHtml(activeCategory)}
                        </span>
                    )}
                </>
            )}

            {/* Sub-category crumb */}
            {activeSubCategory !== 'All' && !isCatMatch(activeCategory, activeSubCategory) && (
                <>
                    <span style={{ color: textColor, opacity: 0.7, margin: '0 2px' }}>
                        {separator}
                    </span>
                    <span style={{
                        color: activeColor,
                        fontWeight: fontWeight,
                        fontSize: 'inherit',
                        fontFamily: 'inherit',
                        textTransform: 'inherit'
                    }}>
                        {stripHtml(activeSubCategory)}
                    </span>
                </>
            )}
        </nav>
    );
};

export default ShopBreadcrumbs;
